import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card,  CardBody, Col, Input,  Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Edit2, Eye } from 'react-feather';
import styled from 'styled-components';
import { SprayProduct, NutritionEntity, SprayExecutedProduct, blankProduct, Product, InventoryProduct } from '../types';

import { ZoomableImage } from './ZoomableImage';
import { useProducts, useUnits } from '../helpers/hooks';
import { useQueryRetrieveInventory } from '../services/stores/queries';
import { ProductDetails } from './ProductDetails';

interface ProductCardProps {
  product: SprayProduct | NutritionEntity | SprayExecutedProduct;
  onEdit?: (product: SprayProduct | NutritionEntity | SprayExecutedProduct, 
            editAmount: number | undefined) => void;
  isRealized?: boolean;
  canEdit?: boolean;
  actualQuantityDescriptions?: string[];
  plannedQuantityDescription?: string;
  message?: string;
}

interface ProductQuantityData  {
    plannedQuantity: number | undefined;
    plannedUnitID: string | undefined;
    actualQuantity: number | undefined;
    actualUnitID: string | undefined;
}

const StyledCardBody = styled(CardBody)`
  width: 100%;
  overflow-x: hidden;
  
  & > div {
    max-width: 100%;
    overflow-x: hidden;
  }
`;

const StyledCard = styled(Card)`
  margin-bottom: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgb(78, 94, 118) ;
  position: relative;
  min-height: 200px;

  .card-header {
    margin-top: 0px;
    margin-bottom: 5px;
    padding-top: 0px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${props => props.index % 2 === 0 ? '#cfe2ff' : '#f8f9fa'};
    border-bottom: 1px solid rgb(22, 111, 200);
      h5 {
        margin: 0;
        font-weight: 600;
        color: blue;
        border-radius: 0.25rem;
      }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 30px 30px 0 0;
    border-color: #0d6efd transparent transparent transparent;
    border-top-left-radius: 8px;
  }
`;


const StyledProductCardRealized = styled(Card)`
  margin-bottom: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgb(202, 13, 13);
  position: relative;
  min-height: 200px;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${props => props.index % 2 === 0 ? '#cfe2ff' : '#f8f9fa'};
    border-bottom: 1px solid #dee2e6;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 30px 30px 0 0;
    border-color:rgb(202, 13, 13) transparent transparent transparent;
    border-top-left-radius: 8px;
  }
`;



const ProductTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgb(22, 111, 200);

  h5 {
    margin: 0;
    font-weight: 600;
    color: blue;
    padding: 0.5rem;
    border-radius: 0.25rem;
  }
`;


const isSprayProduct = (p: SprayProduct | NutritionEntity | SprayExecutedProduct) => {

  return ('treatmentID' in p);
};

const isNutritionProduct = (p: SprayProduct | NutritionEntity | SprayExecutedProduct) => {
return ('remainingAmount' in p);
};

const isSprayExecutedProduct = (p: SprayProduct | NutritionEntity | SprayExecutedProduct) => {
return ('quantity' in p);
};

/**
 * 
 * @param product 
 * @returns 
 */
const productQuantityData = (product: SprayProduct | NutritionEntity | SprayExecutedProduct, 
                            isRealized: boolean) => {

  const anyProduct = product as any;

  if (isNutritionProduct(product)) {
    return {
        plannedQuantity: anyProduct.quantityPlanned,
        plannedUnitID: anyProduct.unitID,
        actualQuantity: isRealized? anyProduct.quantity : undefined,
        actualUnitID: anyProduct.unitID
    }
  }

  if (isSprayProduct(product)) {
      return {
          plannedQuantity: anyProduct.plannedProductQuantity,
          plannedUnitID: anyProduct.plannedProductQuantityUnitID,
          actualQuantity: undefined,
          actualUnitID: anyProduct.plannedProductQuantityUnitID,
      }
  }

  if (isSprayExecutedProduct(product)) {
      return {
          plannedQuantity: anyProduct.plannedProductQuantity,
          plannedUnitID: anyProduct.plannedProductQuantityUnitID,
          actualQuantity: anyProduct.quantity,
          actualUnitID: anyProduct.unitID
      }
  }

 

  return {
      plannedQuantity: anyProduct.quantityPlanned,
      plannedUnitID: anyProduct.unitID,
      actualQuantity: anyProduct.actualQuantity,
      actualUnitID: anyProduct.unitID
  }
};

export const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  onEdit,
  isRealized = false,
  canEdit = false,
  actualQuantityDescriptions = undefined,
  plannedQuantityDescription = undefined,
  message = undefined
}) => {

    const [coreProduct, setCoreProduct] = useState<Product>(blankProduct);
    const [showEditAmount, setShowEditAmount] = useState(false);
    const [editAmount, setEditAmount] = useState(undefined);
    const [showDetails, setShowDetails] = useState(false);

    const { data: inventory, 
      error: errorInventory, 
      isLoading: isLoadingInventory  
} = useQueryRetrieveInventory();


    const { products, toProduct } = useProducts();
    const { toUnitName, toUnitLongName } = useUnits();
  
    useEffect(() => {
      setCoreProduct(toProduct(product.productID));
      setFormState(productQuantityData(product, isRealized));
    }, [product, products]);

    const [formState, setFormState] = useState(productQuantityData(product, isRealized));

    useEffect(() => {
      setFormState(productQuantityData(product, isRealized));
    }, [product, isRealized]);

    const isValidActualQuantity = (value: any) => {

      if ((value === undefined) || (value === null) || (String(value).trim() === "") ) {
        return false;
      }

      const parsedValue = parseFloat(value);
      return !isNaN(parsedValue) && parsedValue >= 0;
    }


    const updateEditAmount = (value: any) => {

      if (isValidActualQuantity(value)) { 
        setEditAmount(value);
      } else {  
        setEditAmount(undefined);
      }
      
    }

    const handleSaveEdit = () => {
      if (isValidActualQuantity(editAmount)) {
        setFormState({ ...formState, actualQuantity : editAmount});
        onEdit?.(product, editAmount);
      }

      setShowEditAmount(false);
    }

    const getProductRemaingQuantity = (product: Product) => {

      const productInventory = inventory?.products.find((p: InventoryProduct) => p.productID === product.productID);
      if (productInventory) {
        return productInventory.quantity.toFixed(2) + ' ' + productInventory.uom;
      }
      return '';
    }


  const TheCardBody = () => (
      <StyledCardBody>
        
        {message &&
          <h5>
          {message}
        </h5>
        }
        <ProductTitle>
            <h5> {coreProduct.productName}</h5>
            <Eye className="align-middle mr-0" color="blue" size={18} onClick={() => {setShowDetails(true)}} />
        </ProductTitle>
                
        <div className="d-flex" style={{ width: '100%' }}>
            <div className="product-image me-2" >
                {(coreProduct.imageURL && coreProduct.imageURL.trim().length > 0) ? (
                    <ZoomableImage 
                    src={coreProduct.imageURL} 
                    alt={coreProduct.productName} 
                    width= {'60px'}
                    height= {'60px'}
                    />
                ) : (
                    <div className="placeholder-image" />
                )}
            </div>

            <div className="product-details " style={{ width: '100%'}}>
                {(plannedQuantityDescription === undefined) && formState.plannedQuantity && formState.plannedUnitID &&         
                        <div className="d-flex align-items-center mb-2" style={{ width: '100%'}}>
                            <div>
                              <label className="text-muted">Programmé:</label>
                              <div className="ms-2 d-inline-block" style={{ paddingRight: '3px' }}>
                                  {formState.plannedQuantity} {toUnitName(formState.plannedUnitID)}
                              </div>
                          </div>
                    </div>
                }
                {(plannedQuantityDescription !== undefined) &&
                        <div className="d-flex align-items-center mb-2" style={{ width: '100%'}}>
                            <div>
                              <label className="text-muted">Programmé:</label>
                              <div className="ms-2 d-inline-block" style={{ paddingRight: '3px' }}>
                                  {plannedQuantityDescription}
                              </div>
                          </div>
                    </div>
                }



            <div className="product-details flex-grow-1">
                {
                  (actualQuantityDescriptions === undefined) && formState.actualQuantity  &&         
                        <div className="d-flex align-items-center mb-2" style={{ width: '95%'}}>
                          <div style={{ width: '95%' }}>
                            <label className="text-muted">Realisé:</label>
                            <div className="ms-2 d-inline-block">
                               {formState.actualQuantity} {toUnitName(formState.actualUnitID)}
                            </div>
                          </div>
                        </div>
                }
                {canEdit &&
                    <div className="ms-2">
                        <button 
                        className="btn btn-link btn-sm p-0"
                        onClick={() => {setShowEditAmount(true); setEditAmount(formState.actualQuantity)}}
                        style={{ cursor: 'pointer', border: 'none' }}
                        >
                        <Edit2 size={16}/>
                        </button>
                    </div>
                }
            </div>


            <div className="product-details" style={{ width: '100%'}}>
                {
                  (actualQuantityDescriptions !== undefined) &&         
                    <div style={{ 
                      width: '100%', 
                      padding: 0,
                      margin: 0
                    }}>
                      <Row className="g-0" style={{ 
                        width: '100%', 
                        margin: 0
                      }}>
                        <Col xs={6} md={2} className="text-muted" style={{ padding: 0 }}>
                          Réalisé:
                        </Col> 
                        <Col xs={6} style={{
                          width: '100%',
                          padding: 0,
                          margin: 0
                        }}>
                          {actualQuantityDescriptions.map((description, index) => (
                            <div key={index} style={{ 
                              width: '100%',
                              padding: 0,
                              margin: 0
                            }}>
                              {description}
                            </div>
                          ))}
                        </Col>
                      </Row>
                    </div>
                }
            </div>

            

            </div>
        </div>


      </StyledCardBody>
  );


  return (
    <>

      {isRealized && 
        <StyledProductCardRealized>
          <TheCardBody />
        </StyledProductCardRealized>
      }

     {!isRealized && 
      <StyledCard>
          <TheCardBody />
      </StyledCard>
      }


    <Modal isOpen={showEditAmount} toggle={() => setShowEditAmount(false)}>
        <ModalHeader toggle={() => setShowEditAmount(false)}>
           Modifier la quantité réalisée 
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label" style={{fontSize: '1.2rem', color: 'blue'}}>{coreProduct.productName} en {toUnitLongName(formState.actualUnitID)}</label>
            <Input
              type="number"
              value={editAmount}
              onChange={(e) => updateEditAmount(e.target.value)}
              className="form-control"
              placeholder= {"Entrer la quantité en " + toUnitName(formState.actualUnitID)}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowEditAmount(false)}>
            Annuler
          </Button>
          <Button color="primary" disabled={!isValidActualQuantity(editAmount)} onClick={handleSaveEdit}>
            Sauvegarder
          </Button>
        </ModalFooter>
      </Modal>


      <Modal isOpen={showDetails} toggle={() => setShowDetails(false)}>
        <ModalHeader toggle={() => setShowDetails(false)}>
           {coreProduct.productName} 
        </ModalHeader>
        <ModalBody>
          <ProductDetails product={coreProduct} />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => setShowDetails(false)}>
            OK
          </Button>
        </ModalFooter>
      </Modal>


    </>
  ) 

};


/*
            {canEdit &&
              <div className="d-flex align-items-center">
                  <div style={{ width: '200px' }}>
                  <div className="ms-2 mt-1">
                      <button 
                      className="btn btn-link btn-sm p-0"
                      onClick={() => {setShowEditAmount(true); setEditAmount(formState.actualQuantity)}}
                      style={{ cursor: 'pointer', border: 'none' }}
                      >
                      <Edit2 size={16}/>
                      </button>
                  </div>
                  </div>
              </div>
              }
*/  

/*
*/